import type {
  AccessState,
  AccountReadiness,
  ServiceAccessMode,
  UserAccessSnapshot,
} from '@/features/commandDeck/contracts/types'
import type { PlanId } from '@/types/mentors'

import { VISITOR_ACCESS_SNAPSHOT } from './constants'

const ACCESS_STATES = new Set<AccessState>([
  'visitor',
  'requested_access',
  'demo_booked',
  'creator_beta',
  'studio_access',
  'founder_mode',
  'admin_mode',
  'enterprise_review',
  'investor_preview',
])

const ACCOUNT_READINESS = new Set<AccountReadiness>([
  'not_ready',
  'pathway_only',
  'ready_for_guided_studio',
  'ready_for_full_app',
])

const SERVICE_ACCESS_MODES = new Set<ServiceAccessMode>(['controlled', 'expanded'])

export interface FirestoreUserAccessFields {
  accessState?: unknown
  accountReadiness?: unknown
  serviceAccessMode?: unknown
  displayName?: unknown
  lastActiveProjectId?: unknown
  planId?: unknown
  roles?: unknown
}

function isAccessState(value: unknown): value is AccessState {
  return typeof value === 'string' && ACCESS_STATES.has(value as AccessState)
}

function parseAccountReadiness(value: unknown): AccountReadiness {
  if (typeof value === 'string' && ACCOUNT_READINESS.has(value as AccountReadiness)) {
    return value as AccountReadiness
  }
  return 'not_ready'
}

function parseServiceAccessMode(value: unknown): ServiceAccessMode {
  if (typeof value === 'string' && SERVICE_ACCESS_MODES.has(value as ServiceAccessMode)) {
    return value as ServiceAccessMode
  }
  return 'controlled'
}

function parseRoles(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((r): r is string => typeof r === 'string')
}

function parsePlanId(value: unknown): PlanId | undefined {
  const allowed: PlanId[] = ['goalbuddy_free', 'synk_basic', 'synk_plus', 'synk_pro', 'admin']
  if (typeof value === 'string' && allowed.includes(value as PlanId)) {
    return value as PlanId
  }
  return undefined
}

function mapAdminAccessState(roles: string[]): AccessState | null {
  return roles.includes('admin') ? 'admin_mode' : null
}

/**
 * Resolve UserAccessSnapshot from Firestore user doc (read-only).
 * Fail-closed: unknown accessState → visitor; planId never auto-elevates to creator_beta.
 */
export function mapLegacyProfileToAccessSnapshot(
  fields: FirestoreUserAccessFields,
  fallbackDisplayName?: string
): UserAccessSnapshot {
  const roles = parseRoles(fields.roles)
  const explicitState = isAccessState(fields.accessState) ? fields.accessState : null
  const bridgedAdmin = mapAdminAccessState(roles)

  let accessState: AccessState = 'visitor'
  if (explicitState) {
    accessState = explicitState
  } else if (bridgedAdmin) {
    accessState = bridgedAdmin
  }

  let accountReadiness = parseAccountReadiness(fields.accountReadiness)
  if (accessState === 'admin_mode' && accountReadiness === 'not_ready') {
    accountReadiness = 'ready_for_full_app'
  } else if (accessState === 'creator_beta' && accountReadiness === 'not_ready') {
    accountReadiness = 'ready_for_guided_studio'
  } else if (
    (accessState === 'requested_access' || accessState === 'demo_booked') &&
    accountReadiness === 'not_ready'
  ) {
    accountReadiness = 'pathway_only'
  } else if (
    (accessState === 'enterprise_review' || accessState === 'investor_preview') &&
    accountReadiness === 'not_ready'
  ) {
    accountReadiness = 'pathway_only'
  }

  const displayName =
    (typeof fields.displayName === 'string' && fields.displayName.trim()) ||
    fallbackDisplayName ||
    VISITOR_ACCESS_SNAPSHOT.displayName

  const lastActiveProjectId =
    typeof fields.lastActiveProjectId === 'string' ? fields.lastActiveProjectId : undefined

  return {
    accessState,
    accountReadiness,
    serviceAccessMode: parseServiceAccessMode(fields.serviceAccessMode),
    displayName,
    lastActiveProjectId,
  }
}

export function buildLegacyProfile(fields: FirestoreUserAccessFields): {
  planId?: PlanId
  roles: string[]
  browseOnly: boolean
  displayName?: string
} {
  const roles = parseRoles(fields.roles)
  const planId = parsePlanId(fields.planId)
  const browseOnly = planId === 'goalbuddy_free' && !roles.includes('admin')

  return {
    planId,
    roles,
    browseOnly,
    displayName: typeof fields.displayName === 'string' ? fields.displayName : undefined,
  }
}
